import React, { useEffect, useRef, useState } from "react";
import UpdateMethodIcon from "icons/dashboard/UpdateMethodIcon";
import BreadCrumbs from "components/ui-component/BreadCrumbs";
import {
  StepConfig,
  SubscriptionData,
  UpdatePaymentMethodProps,
} from "types/subscriptionAndPlans";
import PaymentSubscribePlan from "./PaymentSubscribePlan";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { paymentUpdateSubscriptionData } from "store/slices/dashboardSlice";
import { decode, getProductFrequency } from "utils/HelperFunctions";
import Skeleton from "react-loading-skeleton";
import PaymentResultScreen from "./PaymentResultScreen";
import CustomModal from "components/ui-component/CustomModal";
import UpdatePaymentConfirm from "components/subscriptions/UpdatePaymentConfirm";
import {
  createPaypalPaymentToken,
  updateLogoAndFavicon,
  updatePaymentPermission,
  verifyWalletPay,
} from "store/slices/paymentsSlice";
import {
  addNotification,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "store/store";
import { updateSubscriptionPaymentMethod } from "store/slices/subscriptionSlice";
import { CloseIcon } from "icons/close/CloseIcon";

const UpdatePaymentMethod = ({
  id,
  show,
  setShow,
  setId,
  fetchDetails,
  setIsPreviewId = () => {},
  setIsPreview = () => {},
  setHandleDrawerSize = () => {},
}: UpdatePaymentMethodProps) => {
  const [currentStep, setCurrentStep] = React.useState<number>(1);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [errrorMessage, setErrorMessage] = useState<string | null>(null);
  const [screen, setScreen] = useState<boolean>(false);
  const [products, setProducts] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionData>(
    {} as SubscriptionData
  );
  const { conFee, payPalOrder } = useAppSelector(
    (state: RootState) => state.payment
  );
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const userData = useUserData();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isBTNLoading, setIsBTNLoading] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();
  const updateRef = useRef<any>();

  const addPaymentMethodChangedNotification = async (details?: {
    subscriptionId?: string | null;
    clusterId?: string | null;
    paymentAmount?: number | string | null;
  }) => {
    const subscription = decode(id || "")?.split("_");
    const subscriptionId =
      details?.subscriptionId || currentPlan?.id || subscription?.[0];

    const comment =
      currentPlan?.recurring_type === "subscription"
        ? "subscription"
        : currentPlan?.plan_id > 0
          ? "payment agreement"
          : "custom plan";

    await dispatch(
      addNotification({
        initiated_by: "contact",
        event_type: "payment_method",
        userid: userData?.data?.accountData?.leadData?.user_id,
        email: "",
        event_status: "updated",
        activity_type: "payment_activity",
        clusterid:
          details?.clusterId || currentPlan?.clusterid || subscription?.[1],
        communication_status: "",
        accounts_id: userData?.data?.accountData?.id,
        payment_amount:
          details?.paymentAmount || currentPlan?.amount || totalAmount,
        sch_id: subscriptionId,
        subscription_id: subscriptionId,
        comments: comment,
        plan_id: currentPlan?.plan_id > 0 ? currentPlan?.plan_id : 0,
      })
    );
  };

  const handleOnClick = (id: number) => {
    if (!paymentSuccess) {
      setCurrentStep(id);
      setPaymentSuccess(false);
      setScreen(false);
    }
  };
  const [isManualForm, setIsManualForm] = useState<boolean>(true);

  const getPlanData = async () => {
    try {
      let payload = {
        slug: id,
      };

      let res: any = await dispatch(
        paymentUpdateSubscriptionData(payload)
      ).unwrap();

      if (res?.body?.success) {
        let { data } = res?.body;

        const { customerData, paymentPermission, companyLogoDetails } = data;

        dispatch(updatePaymentPermission(paymentPermission));
        dispatch(
          updateUserData({
            ...userData?.data?.accountData,
            ...customerData,
          })
        );
        dispatch(updateLogoAndFavicon(companyLogoDetails));

        let { subscriptionData } = data;

        let { productSubscriptions } = subscriptionData;
        let totalAmountCalc = 0;
        if (productSubscriptions?.length) {
          const schedule = productSubscriptions?.[0]?.priceDetails?.schedule;
          const interval_custom_count =
            productSubscriptions?.[0]?.priceDetails?.interval_custom_count;
          const interval_custom_selector =
            productSubscriptions?.[0]?.priceDetails?.interval_custom_selector;

          const freq = getProductFrequency(
            schedule,
            interval_custom_count,
            interval_custom_selector
          );
          let arr = [];
          for (let i = 0; i < productSubscriptions?.length; i++) {
            let products = productSubscriptions[i];
            totalAmountCalc += products?.amount * products?.quantity;
            arr.push({
              id: products?.productDetails?.id,
              name: products?.productDetails?.name,
              amount: products?.amount,
              pid: products?.prod_price_id,
              frequency: freq?.value || "month",
              quantity: products?.quantity,
              image_name: products?.productDetails?.image_name,
              image_path: products?.productDetails?.image_path,
              interval_custom_count:
                products?.priceDetails?.interval_custom_count || 1,
              interval_custom_selector:
                products?.priceDetails?.interval_custom_selector || "month",
            });
          }

          setProducts(arr);
          setTotalAmount(subscriptionData?.amount || totalAmountCalc);
        }

        setCurrentPlan(subscriptionData);
      }
    } catch (error) {
      console.log("getPlanData error =====", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && id) {
      getPlanData();
    }
  }, [show, id]);

  const handleCappAndBank = async (
    method: string,
    clusterId: string | null,
    lead_id: any,
    subsId: string | null
  ) => {
    let payment_method =
      method === "cashapp"
        ? "CAPP"
        : method === "card"
          ? "CC"
          : method === "amazonPay"
            ? "AMZP"
            : "ACH";

    try {
      let payload = {
        lead_id: lead_id,
        cluster_id: clusterId,
        isrecurring: "Y",
        x_payment_method: payment_method,
        amount: searchParams.get("amount") || 0,
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData.phone,
        module: "process_payment",
        transaction_id: searchParams.get("setup_intent"),
        isScheduleOnetime: true,
        schedule_id: subsId,
        conv_fee: conFee,
        isPayForProduct: true,
        description: "Online Payment",
        payment_note: "Subscription",
        custom_conv: "on",
      };
      let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
      if (res?.body?.success && !res?.body?.data?.isDuplicate) {
        let payload = {
          subscription_id: subsId,
          cpp_id: res?.body?.data?.cpp_id || "",
        };

        let result: any = await dispatch(
          updateSubscriptionPaymentMethod(payload)
        ).unwrap();

        if (result?.body?.success) {
          await addPaymentMethodChangedNotification({
            subscriptionId: subsId,
            clusterId,
            paymentAmount: searchParams.get("amount"),
          });
          setPaymentSuccess(true);
          setScreen(true);
        } else {
          setPaymentSuccess(true);
          setScreen(false);
        }
      } else {
        setPaymentSuccess(true);
        setScreen(false);
      }
    } catch (error) {
      setPaymentSuccess(true);
      setScreen(false);
      console.log("handleCapp error =====", error);
    } finally {
      setIsScreenLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.has("subsDrawer") && id) {
      if (
        searchParams.has("method") &&
        !searchParams.has("checked") &&
        searchParams.has("clusterid")
      ) {
        setCurrentStep(2);
        searchParams.set("checked", "true");
        if (
          searchParams.get("method") === "cashapp" ||
          searchParams.get("method") === "bank" ||
          searchParams.get("method") === "card" ||
          searchParams.get("method") === "amazonPay"
        ) {
          setIsScreenLoading(true);
          handleCappAndBank(
            searchParams.get("method") || "cashapp",
            searchParams.get("clusterid"),
            searchParams.get("lead_id"),
            searchParams.get("subsId")
          );
        }
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      }
    }
  }, [id]);

  const handlePayPal = async () => {
    setIsScreenLoading(true);
    searchParams.set("checked", "true");
    const tokendata = {
      token: payPalOrder?.token,
    };
    try {
      let resp: any = await dispatch(
        createPaypalPaymentToken(tokendata)
      ).unwrap();
      if (resp && resp?.body && resp?.body?.success) {
        let payload = {
          subscription_id: searchParams.get("subsId"),
          cpp_id: resp?.body?.data?.cppid || "",
        };

        let res: any = await dispatch(
          updateSubscriptionPaymentMethod(payload)
        ).unwrap();

        if (res?.body?.success) {
          await addPaymentMethodChangedNotification({
            subscriptionId: searchParams.get("subsId"),
            clusterId: searchParams.get("clusterid"),
            paymentAmount: searchParams.get("amount"),
          });
          setPaymentSuccess(true);
          setCurrentStep(2);
          setScreen(true);
        } else {
          setPaymentSuccess(true);
          setCurrentStep(2);
          setScreen(false);
        }
        setTimeout(() => setIsScreenLoading(false), 1000);
      }
    } catch (error) {
      console.log("handlePayPal error =====", error);
      setPaymentSuccess(true);
      setCurrentStep(2);
      setScreen(false);
    }
  };

  useEffect(() => {
    if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.has("amount") &&
      searchParams.get("method") === "paypal" &&
      searchParams.get("subsDrawer") === "true"
    ) {
      searchParams.set("checked", "true");
      setCurrentStep(2);
      handlePayPal();
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  }, [id]);

  const onClose = () => {
    setShow(false);
    setId(undefined);
    if (paymentSuccess) {
      fetchDetails();
    }
    setPaymentSuccess(false);
    setCurrentStep(1);
    setIsLoading(true);
  };

  const steps: StepConfig[] = [
    {
      id: 0,
      title: "Subscriptions & Plans",
    },
    {
      id: 1,
      title: "Payment method",
    },
    {
      id: 2,
      title: "Confirm",
    },
  ];

  return (
    <div
      className={`relative w-full bg-white h-full overflow-y-auto  lg:pt-10 pt-5 `}
    >
      <div className="flex items-center justify-between w-full lg:pb-[30px] pb-3 lg:px-10 px-5">
        <div className="flex items-center gap-2">
          <UpdateMethodIcon />
          <p className="text-[26px] text-txtPrimary font-medium leading-[30px]">
            Update payment method
          </p>
        </div>
        <span
          onClick={() => onClose()}
          className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
        >
          <CloseIcon size={30} />
        </span>
      </div>
      <div className="w-full lg:py-5 py-2 lg:px-10 px-5 ">
        <BreadCrumbs
          steps={steps}
          currentStep={currentStep}
          handleOnClick={handleOnClick}
        />
      </div>
      <div>
        {isLoading || isScreenLoading ? (
          <div className="lg:pt-5 pt-[10px] w-full lg:gap-5 gap-3 flex flex-col lg:px-10 px-5">
            <div className="lg:text-2xl text-base text-txtPrimary flex flex-col lg:gap-2 gap-1 lg:pb-5 pb-[10px]">
              <p className="font-medium ">Select a payment method</p>
              <p className="lg:text-base text-sm text-txtParagraph">
                Select a payment method to use with your{" "}
                <Skeleton width={80} height={20} />
              </p>
            </div>

            <div className="flex flex-col lg:gap-6 gap-5 max-w-[530px]">
              <div className="flex w-full justify-between items-center">
                <div className="flex lg:gap-4 gap-2">
                  <input
                    className="customRadio focus-visible:outline-none cursor-pointer"
                    type="radio"
                    checked={false}
                    onChange={() => {}}
                  />
                  <span
                    className="cursor-not-allowed pointer-events-none"
                    onClick={() => {}}
                  >
                    <Skeleton width={200} height={20} />
                  </span>
                </div>
                <span className="text-sm text-txtPrimary">
                  <Skeleton width={80} height={20} />
                </span>
              </div>

              <div className="flex w-full justify-between items-center">
                <div className="flex lg:gap-4 gap-2">
                  <input
                    className="customRadio focus-visible:outline-none cursor-pointer"
                    type="radio"
                    checked={false}
                    onChange={() => {}}
                  />
                  <span
                    className="cursor-not-allowed pointer-events-none"
                    onClick={() => {}}
                  >
                    <Skeleton width={200} height={20} />
                  </span>
                </div>
                <span className="text-sm text-txtPrimary">
                  <Skeleton width={80} height={20} />
                </span>
              </div>
            </div>

            <div className="w-full max-w-[530px] flex flex-col gap-5 pb-5">
              <Skeleton width={530} height={40} />
            </div>
          </div>
        ) : (
          <>
            {currentStep === 1 && (
              <PaymentSubscribePlan
                id={id}
                newProducts={products}
                currentPlan={currentPlan}
                amount={totalAmount || 0}
                setPaymentSuccess={setPaymentSuccess}
                setCurrentStep={setCurrentStep}
                setScreen={setScreen}
                isManualForm={isManualForm}
                setIsManualForm={setIsManualForm}
                ref={updateRef}
                isDowngradePlan={false}
                isUpdateSubscriptionPaymentMethod={true}
                setShowPopup={setShowPopup}
                setIsBTNLoading={setIsBTNLoading}
                onPaymentMethodChanged={() =>
                  addPaymentMethodChangedNotification()
                }
              />
            )}
            {currentStep === 2 && paymentSuccess && (
              <PaymentResultScreen
                screen={screen}
                errrorMessage={errrorMessage}
                id={id}
                setHandleDrawerSize={setHandleDrawerSize}
                setIsPreview={setIsPreview}
                setIsPreviewId={setIsPreviewId}
                isPaymentPlan={
                  products?.length <= 0 || currentPlan?.plan_id > 0
                }
                onClose={onClose}
              />
            )}
          </>
        )}
      </div>

      <CustomModal
        signModal={true}
        isSubscribeModal={true}
        isOpen={showPopup}
        title="Alert"
        doneText="OK"
        className="max-w-[600px] w-full h-fit rounded-[10px] lg:overflow-y-hidden overflow-y-auto"
        cancleText="Cancel"
        onDoneClick={() => {
          if (updateRef.current) {
            updateRef.current.Click();
          }
        }}
        onCloseClick={() => {
          if (!isBTNLoading) setShowPopup(false);
        }}
      >
        <UpdatePaymentConfirm
          setShowPopup={setShowPopup}
          isBTNLoading={isBTNLoading}
          handleUpdate={() => {
            if (updateRef.current) {
              updateRef.current.callHandleClick();
            }
          }}
        />
      </CustomModal>
    </div>
  );
};

export default UpdatePaymentMethod;
